import React, { useCallback, useEffect, useRef, useState } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import ReactTooltip from "react-tooltip";
import classNames from "classnames";
import s from "./styles.scss";
import { IAudienceMetrics } from "../../../types/services/IMetrics";
import {
  getPostAudienceMetrics,
  populatePostAudienceMetrics,
} from "../../../utils/fetch";
import { getCouldNotSend } from "../../../utils/helpers";

interface Props {
  lineClassName: string;
  notTargeted?: number;
  charityId?: string;
  postId?: string;
  searchUrl?: string;
  token?: string;
  tooltipId: string;
  strongLabels?: boolean;
}

const MetricLabel: React.FC<{ strong?: boolean; text: string }> = ({
  strong,
  text,
}) => (strong ? <strong>{text}</strong> : <>{text}</>);

const PostNotTargetedMetrics: React.FC<Props> = ({
  lineClassName,
  notTargeted,
  charityId,
  postId,
  searchUrl,
  token,
  tooltipId,
  strongLabels,
}) => {
  const hasRtdbCount = typeof notTargeted === "number";
  const canExpand = !hasRtdbCount || notTargeted > 0;
  const [expanded, setExpanded] = useState(false);
  const [metrics, setMetrics] = useState<IAudienceMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadAudience = useCallback(
    (shouldBackfill: boolean) => {
      if (!searchUrl || !charityId || !postId || !token) {
        setLoadError(true);
        return;
      }
      let cancelled = false;
      setLoading(true);
      setLoadError(false);
      const request = shouldBackfill
        ? populatePostAudienceMetrics(searchUrl, charityId, postId, token)
        : getPostAudienceMetrics(searchUrl, charityId, postId, token);
      request
        .then(response => {
          if (!cancelled && response) {
            setMetrics(response);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLoadError(true);
            setLoading(false);
          }
        });
      return () => {
        cancelled = true;
      };
    },
    [charityId, postId, searchUrl, token]
  );

  const prevPostId = useRef(postId);
  useEffect(() => {
    if (prevPostId.current === postId) {
      return;
    }
    prevPostId.current = postId;
    setExpanded(false);
    setMetrics(null);
    setLoadError(false);
  }, [postId]);

  useEffect(() => {
    if (!canExpand && expanded) {
      setExpanded(false);
    }
  }, [canExpand, expanded]);

  const collapsedCount = hasRtdbCount ? notTargeted : "…";

  const onToggle = () => {
    if (!canExpand) {
      return;
    }
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (metrics === null && !loading) {
      loadAudience(!hasRtdbCount);
    }
  };

  return (
    <div className={s.row}>
      <p className={lineClassName}>
        <strong>Not targeted:</strong> {collapsedCount}
        {canExpand && (
          <>
            {" "}
            <button
              type="button"
              className={s.toggle}
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label="Not targeted details"
            >
              <i
                className={classNames(
                  "fas",
                  expanded ? "fa-chevron-up" : "fa-chevron-down",
                  s.chevron
                )}
              />
            </button>
          </>
        )}
      </p>
      {expanded && (
        <div>
          {loading && (
            <p className={classNames(lineClassName, s.child)}>Loading…</p>
          )}
          {loadError && !loading && (
            <p className={classNames(lineClassName, s.child)}>
              Could not load skip reasons
            </p>
          )}
          {metrics && !loading && (
            <FragmentLines
              lineClassName={lineClassName}
              metrics={metrics}
              tooltipId={tooltipId}
              strongLabels={strongLabels}
            />
          )}
        </div>
      )}
    </div>
  );
};

const FragmentLines: React.FC<{
  lineClassName: string;
  metrics: IAudienceMetrics;
  tooltipId: string;
  strongLabels?: boolean;
}> = ({ lineClassName, metrics, tooltipId, strongLabels }) => {
  const couldNotSend = getCouldNotSend(metrics);
  const tipId = `${tooltipId}-could-not-send`;
  return (
    <>
      <p className={classNames(lineClassName, s.child)}>
        <MetricLabel strong={strongLabels} text="Unsubscribed:" />{" "}
        {metrics.unsubscribed}
      </p>
      <p className={classNames(lineClassName, s.child)}>
        <MetricLabel strong={strongLabels} text="Deactivated:" />{" "}
        {metrics.deactivated}
      </p>
      <p className={classNames(lineClassName, s.child)}>
        <MetricLabel strong={strongLabels} text="Duplicate contact:" />{" "}
        {metrics.duplicated}
      </p>
      <p className={classNames(lineClassName, s.child)}>
        <MetricLabel strong={strongLabels} text="Could not send:" />{" "}
        {couldNotSend}{" "}
        <span data-tip data-for={tipId}>
          <i className={`fa fa-question-circle ${s.helpIcon}`} />
        </span>
        <ReactTooltip id={tipId} type="info" place="bottom" effect="float">
          No phone or email, unreachable, notifications off, or donor record
          gone.
        </ReactTooltip>
      </p>
    </>
  );
};

export default withStyles(s)(PostNotTargetedMetrics);
